import { useState } from 'react';
import type { CreateMetingInput, GeschatteKmStand } from '@fleet/shared';
import { fmtDate } from '@fleet/shared';
import { Button, Input } from '../../design-system';
import { Modal } from '../../components/ui/Modal';

/**
 * Popup: log an odometer reading by hand.
 *
 * The only source the electric cars have, because a charge point never asks for
 * the odometer — twelve of the fleet had no mileage history at all before this.
 *
 * It warns rather than blocks when the reading is below one we already have.
 * The card shows the *highest* trusted reading, since a meter does not run
 * backwards, so a lower one is stored but does not change the headline figure.
 * Without a word about it, saving looks broken.
 */
export function KmStandToevoegen({
  kenteken,
  huidig,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  kenteken: string;
  /** The reading currently on the card, so a lower entry can be flagged. */
  huidig: GeschatteKmStand | null;
  busy?: boolean;
  /** Failure reported by the caller's save, shown next to the form. */
  error?: string;
  onSubmit: (input: CreateMetingInput) => void;
  onCancel: () => void;
}) {
  const [stand, setStand] = useState('');
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [notitie, setNotitie] = useState('');
  const [fout, setFout] = useState('');

  // Alleen cijfers overhouden, zodat "141.200 km" net zo goed werkt als 141200.
  const waarde = Number(stand.replace(/[^\d]/g, ''));
  const geldig = Number.isFinite(waarde) && waarde > 0;
  const lager = geldig && huidig != null && waarde < huidig.kmStand;

  function opslaan(e: React.FormEvent) {
    e.preventDefault();
    if (!geldig) {
      setFout('Vul een kilometerstand in.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      setFout('Vul een datum in — die bepaalt waar de stand in de grafiek komt.');
      return;
    }
    setFout('');
    onSubmit({ waarde, datum, notitie: notitie.trim() || null });
  }

  return (
    <Modal open onClose={onCancel} title="Kilometerstand toevoegen" width={460}>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666666', lineHeight: 1.5 }}>
        Wordt gelogd op{' '}
        <strong style={{ color: '#111111', fontFamily: "'Roboto Mono', monospace" }}>
          {kenteken}
        </strong>{' '}
        en telt mee in de kilometergrafiek en de kosten per kilometer. Terug te vinden — en te
        verwijderen — in de tijdlijn.
      </p>

      <form onSubmit={opslaan} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          label="Kilometerstand"
          value={stand}
          placeholder="bv. 141.200"
          inputMode="numeric"
          onChange={(e) => setStand(e.target.value)}
          autoFocus
        />
        <Input
          label="Datum meting"
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
        />
        <Input
          label="Notitie"
          value={notitie}
          placeholder="optioneel — bv. afgelezen bij de garage"
          onChange={(e) => setNotitie(e.target.value)}
        />

        {lager && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              background: '#FDF6E7',
              border: '1px solid #E0A943',
              borderRadius: 4,
              padding: '10px 12px',
              fontSize: 12.5,
              color: '#7A5A16',
              lineHeight: 1.5,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#E0A943',
                flex: 'none',
                marginTop: 5,
              }}
            />
            <span>
              Er staat al een hogere stand van {huidig!.kmStand.toLocaleString('nl-NL')} km op{' '}
              {fmtDate(huidig!.datum)}
              {huidig!.bron === 'PAS' ? ' uit de tankpasdata' : ''}. Deze meting wordt wel bewaard,
              maar de teller op de kaart blijft op die hogere stand staan.
            </span>
          </div>
        )}

        {(fout || error) && (
          <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{fout || error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Opslaan…' : 'Stand opslaan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
